import { Assignment, Close } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { ApiTargetWithCount } from "griptonite-api-client";
import { sumBy } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useVenueZonesByUuid } from "@/hooks/routes";
import { useTargets, useUpdateTargetsMutation } from "@/hooks/targets";
import { useCurrentVenueUuid } from "@/hooks/venues";
import getAscentStyleLabel from "@/utils/getAscentStyleLabel";

import { SteppableNumberInput } from "../forms/SteppableNumberInput";

const TargetHeader = ({
  target,
  onClose,
}: {
  target?: ApiTargetWithCount;
  onClose: () => void;
}) => {
  return (
    <Box
      sx={{
        height: 50,
        padding: 1,
        fontSize: 16,
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid",
        borderLeft: "none",
        borderColor: "#00000033",
        backgroundColor: "#f5f5f5",
        borderTopLeftRadius: 16,
      }}
    >
      <Typography level="title-lg">
        {target?.gradeValue ? `Grade: ${target.gradeValue}` : "Target not found"}
      </Typography>
      <IconButton
        size="sm"
        sx={{
          borderRadius: 100,
        }}
        onClick={onClose}
      >
        <Close />
      </IconButton>
    </Box>
  );
};

type TargetFormValues = {
  [key: string]: {
    targetCount: number;
  };
};

export default function TargetSidebar({
  uuid,
  climbType,
}: {
  uuid: string;
  climbType: "BOULDER" | "SPORT";
}) {
  const venueUuid = useCurrentVenueUuid();
  const selectedTargetRef = useRef<HTMLDivElement>(null);

  const targetsQuery = useTargets(venueUuid, climbType);
  // const config = targetsQuery.data?.config;

  const zonesByUuid = useVenueZonesByUuid(venueUuid, climbType);

  const target = useMemo(
    () => targetsQuery.data?.targets.find((t) => t.uuid === uuid),
    [targetsQuery.data, uuid],
  );
  const { gradeTargets, currentTotalCount } = useMemo(() => {
    // Find all targets with the same grade
    const gradeTargets =
      targetsQuery.data?.targets.filter((t) => t.gradeValue === target?.gradeValue) || [];
    const currentTotalCount = sumBy(gradeTargets, (t) => t.targetCount);
    gradeTargets.sort((a, b) => {
      // Sort by zone name if zone is set
      if (a.zone && b.zone) {
        const zoneAName = zonesByUuid?.[a.zone]?.name || "";
        const zoneBName = zonesByUuid?.[b.zone]?.name || "";
        return zoneAName.localeCompare(zoneBName);
      }
      return 0;
    });
    return { gradeTargets, currentTotalCount };
  }, [targetsQuery.data, target?.gradeValue, zonesByUuid]);

  const navigate = useNavigate();

  const mutation = useUpdateTargetsMutation(venueUuid);

  const handleClose = () => {
    navigate({
      from: "/gym/$venueUuid/settings/targets/$climbType/$targetUuid",
      to: "..",
      replace: true,
    });
  };

  const methods = useForm<TargetFormValues>({
    defaultValues: Object.fromEntries(
      gradeTargets.map((t) => [t.uuid, { targetCount: t.targetCount }]),
    ),
  });

  useEffect(() => {
    methods.reset(
      Object.fromEntries(gradeTargets.map((t) => [t.uuid, { targetCount: t.targetCount }])),
    );
  }, [gradeTargets, methods]);

  // Scroll to the selected target when the component mounts or uuid changes
  useEffect(() => {
    if (selectedTargetRef.current) {
      selectedTargetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [uuid, gradeTargets]); // Include gradeTargets to ensure the elements are rendered

  const onSubmit = async (values: TargetFormValues) => {
    mutation.mutate(
      Object.entries(values).map(([uuid, { targetCount }]) => ({
        uuid,
        targetCount,
      })),
    );
  };

  const setToCurrent = () => {
    gradeTargets.forEach((t) => {
      methods.setValue(t.uuid, { targetCount: t.currentCount ?? 0 });
    });
  };

  return (
    <Stack
      sx={{
        height: "100%",
        borderTopLeftRadius: 16,
        overflow: "hidden",
        width: 350,
        borderLeft: "1px solid",
        borderLeftColor: "neutral.200",
      }}
    >
      <TargetHeader target={target} onClose={handleClose} />
      <FormProvider {...methods}>
        <Stack
          flex={1}
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          overflow="hidden"
        >
          <Stack padding={2} gap={2} flex={1} sx={{ overflowY: "auto" }}>
            <Typography>
              Current total: <Typography fontWeight="bold">{currentTotalCount ?? 0}</Typography>{" "}
              route{currentTotalCount === 1 ? "" : "s"}
            </Typography>

            <Box>
              <Button
                onClick={setToCurrent}
                size="sm"
                color="neutral"
                variant="outlined"
                startDecorator={<Assignment fontSize="small" />}
              >
                Load targets from current set
              </Button>
            </Box>

            {gradeTargets.map((gradeTarget) => {
              let labelName;
              if (gradeTarget.zone) {
                labelName = zonesByUuid?.[gradeTarget.zone]?.name || "Unknown Zone";
              } else if (gradeTarget.ascentStyle) {
                labelName = getAscentStyleLabel(gradeTarget.ascentStyle);
              }
              if (gradeTarget.uuid === uuid) {
                return (
                  <Box
                    key={gradeTarget.uuid}
                    ref={selectedTargetRef}
                    sx={{
                      backgroundColor: "primary.100",
                      marginX: -2,
                      padding: 2,
                    }}
                  >
                    <SteppableNumberInput
                      name={`${gradeTarget.uuid}.targetCount`}
                      label={labelName || "Desired target"}
                      min={0}
                      step={1}
                    />
                  </Box>
                );
              }
              return (
                <SteppableNumberInput
                  key={gradeTarget.uuid}
                  name={`${gradeTarget.uuid}.targetCount`}
                  label={labelName || "Desired target"}
                  min={0}
                  step={1}
                />
              );
            })}
          </Stack>
          <Box sx={{ padding: 1.5 }}>
            <Button size="lg" type="submit" fullWidth disabled={!methods.formState.isDirty}>
              Save
            </Button>
          </Box>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
